import { useEffect, useState } from 'react'
import { BellRing } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api/client'
import type { HelmNotificationPreferences, HelmNotificationSeverity } from '@/lib/api/types'

const THRESHOLDS: Array<{ value: HelmNotificationSeverity; label: string }> = [
  { value: 'URGENT', label: 'Only urgent ones' },
  { value: 'ATTENTION', label: 'Urgent and needs attention' },
  { value: 'INFO', label: 'Everything' },
]

/** Whether the bell's notifications also reach this person by email. Off until they turn it on. */
export function NotificationPreferencesCard() {
  const [preferences, setPreferences] = useState<HelmNotificationPreferences | null>(null)
  const [status, setStatus] = useState<string>('')

  useEffect(() => {
    apiClient
      .getNotificationPreferences()
      .then(setPreferences)
      .catch(() => setStatus("Couldn't load your notification settings."))
  }, [])

  async function save(next: HelmNotificationPreferences) {
    const previous = preferences
    setPreferences(next)
    setStatus('Saving…')
    try {
      setPreferences(await apiClient.setNotificationPreferences(next))
      setStatus('Saved')
    } catch {
      setPreferences(previous)
      setStatus("Couldn't save. Try again.")
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BellRing className="h-5 w-5" />
          Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Everything that needs you shows under the bell. Email is extra and off until you turn it
          on.
        </p>
        <div className="flex items-center gap-2">
          <Checkbox
            id="notification-email"
            checked={preferences?.email_enabled ?? false}
            disabled={!preferences}
            onCheckedChange={(checked) =>
              preferences && save({ ...preferences, email_enabled: checked === true })
            }
          />
          <Label htmlFor="notification-email">Email me notifications</Label>
        </div>
        <div className="grid gap-1.5 sm:max-w-xs">
          <Label htmlFor="notification-threshold">Which ones</Label>
          <Select
            value={preferences?.email_min_severity ?? 'URGENT'}
            disabled={!preferences?.email_enabled}
            onValueChange={(value) =>
              preferences &&
              save({ ...preferences, email_min_severity: value as HelmNotificationSeverity })
            }
          >
            <SelectTrigger id="notification-threshold" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THRESHOLDS.map((threshold) => (
                <SelectItem key={threshold.value} value={threshold.value}>
                  {threshold.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="min-h-5 text-xs text-muted-foreground" aria-live="polite">
          {status}
        </p>
      </CardContent>
    </Card>
  )
}
